import { LangLocale, languages } from '@myre/common-i18n';
import { uppercaseFirst } from '@myre/common-primitives';

import { inject, Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import {
  addDays,
  addMonths,
  addYears,
  format,
  formatISO,
  getDate,
  getDay,
  getDaysInMonth,
  getMonth,
  getYear,
  isValid,
  parse,
  parseISO,
  type Day,
  type Month,
} from 'date-fns';

import { LANGLOCALE } from './langlocale';

/**
 * Creates an array and fills it with values.
 */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const valuesArray = Array(length);

  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }

  return valuesArray;
}

@Injectable({ providedIn: 'root' })
export class DateFnsAdapter extends DateAdapter<Date> {
  private _localeData: {
    firstDayOfWeek: number;
    longMonths: string[];
    shortMonths: string[];
    narrowMonths: string[];
    dates: string[];
    longDaysOfWeek: string[];
    shortDaysOfWeek: string[];
    narrowDaysOfWeek: string[];
  };

  private _langLocale = inject(LANGLOCALE, { optional: true });

  constructor() {
    super();

    this.setLocale(this._langLocale);
  }

  public override setLocale(langLocale: LangLocale): void {
    super.setLocale(langLocale);

    const locale = languages[langLocale].dateFnsLocale;

    this._localeData = {
      firstDayOfWeek: locale.options.weekStartsOn,
      longMonths: range(12, i =>
        uppercaseFirst(locale.localize.month(i as Month)),
      ),
      shortMonths: range(12, i =>
        uppercaseFirst(
          locale.localize.month(i as Month, { width: 'abbreviated' }),
        ),
      ),
      narrowMonths: range(12, i =>
        uppercaseFirst(locale.localize.month(i as Month, { width: 'narrow' })),
      ),
      dates: range(31, i => format(new Date(2017, 0, i + 1), 'd')),
      longDaysOfWeek: range(7, i =>
        uppercaseFirst(locale.localize.day(i as Day)),
      ),
      shortDaysOfWeek: range(7, i =>
        uppercaseFirst(locale.localize.day(i as Day, { width: 'abbreviated' })),
      ),
      narrowDaysOfWeek: range(7, i =>
        uppercaseFirst(locale.localize.day(i as Day, { width: 'short' })),
      ),
    };
  }

  public getYear(date: Date): number {
    return getYear(date);
  }

  public getMonth(date: Date): number {
    return getMonth(date);
  }

  public getDate(date: Date): number {
    return getDate(date);
  }

  public getDayOfWeek(date: Date): number {
    return getDay(date);
  }

  public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') {
      return this._localeData.longMonths;
    }

    if (style === 'short') {
      return this._localeData.shortMonths;
    }

    return this._localeData.narrowMonths;
  }

  public getDateNames(): string[] {
    return this._localeData.dates;
  }

  public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') {
      return this._localeData.longDaysOfWeek;
    }

    if (style === 'short') {
      return this._localeData.shortDaysOfWeek;
    }

    return this._localeData.narrowDaysOfWeek;
  }

  public getYearName(date: Date): string {
    return this.getYear(date).toString();
  }

  public getFirstDayOfWeek(): number {
    return this._localeData.firstDayOfWeek;
  }

  public getNumDaysInMonth(date: Date): number {
    return getDaysInMonth(date);
  }

  public clone(date: Date): Date {
    return new Date(date);
  }

  public createDate(year: number, month: number, date: number): Date {
    return new Date(year, month, date);
  }

  public today(): Date {
    return new Date();
  }

  public parse(value: any, parseFormats?: string | string[]): Date | null {
    if (value && typeof value === 'string') {
      if (!parseFormats) {
        return parseISO(value);
      }

      parseFormats = Array.isArray(parseFormats)
        ? parseFormats
        : [parseFormats];

      for (const parseFormat of parseFormats) {
        const d = parse(value, parseFormat, new Date());

        if (isValid(d)) {
          return d;
        }
      }
    }

    return value ? new Date(value) : null;
  }

  public format(date: Date, displayFormat: string): string {
    return format(date, displayFormat, {
      locale: languages[this._langLocale].dateFnsLocale,
    });
  }

  public addCalendarYears(date: Date, years: number): Date {
    return addYears(date, years);
  }

  public addCalendarMonths(date: Date, months: number): Date {
    return addMonths(date, months);
  }

  public addCalendarDays(date: Date, days: number): Date {
    return addDays(date, days);
  }

  public toIso8601(date: Date): string {
    return formatISO(date);
  }

  public override deserialize(value: any): Date | null {
    let date: Date;

    if (this.isDateInstance(value)) {
      return this.clone(value);
    }

    if (typeof value === 'string') {
      if (!value) {
        return null;
      }

      date = this.parse(value);
    }

    if (date && this.isValid(date)) {
      return date;
    }

    return super.deserialize(value);
  }

  public isDateInstance(obj: any): boolean {
    return obj instanceof Date;
  }

  public isValid(date: Date): boolean {
    return isValid(date);
  }

  public invalid(): Date {
    return new Date(NaN);
  }
}
